import { getEvmClient } from '@1delta/providers'
import { encodeAbiParameters, keccak256, pad } from 'viem'
import { KelpWithdrawalManagerAbi } from '../abis/kelp'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/** Storage slot for `userAssociatedNonces` in `LRTWithdrawalManager`.
 *
 *  Derived empirically by walking the inheritance chain:
 *  - `LRTConfigRoleChecker` + `PausableUpgradeable` +
 *    `ReentrancyGuardUpgradeable` + the inherited `lrtConfig`
 *    storage contribute 151 base slots.
 *  - `minRsEthAmountToWithdraw[ETH]` (source slot 0) lives at on-chain
 *    slot 151, `nextUnusedNonce[ETH]` (source slot 2) at slot 153,
 *    `userAssociatedNonces` (source slot 5) at slot 156. */
const KELP_USER_NONCES_SLOT = 156n

/** Sentinel address Kelp uses internally for the ETH asset key.
 *  Enumerated against the deque for the ETH withdrawal route; other
 *  whitelisted collateral can be added to `KELP_WITHDRAWABLE_ASSETS`
 *  as Kelp expands the list. */
const KELP_ETH_SENTINEL = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'

/** Allowlist of assets to probe per user. ETH is the production
 *  path; LST collaterals will be added once Kelp documents the keys
 *  for ETHx / stETH withdrawal routes. */
const KELP_WITHDRAWABLE_ASSETS: string[] = [KELP_ETH_SENTINEL]

/** Storage layout of `DoubleEndedQueue.Uint256Deque`:
 *  - slot 0 (the mapping value slot itself): `uint128 _begin |
 *    uint128 _end` packed
 *  - subsequent slots: `mapping(uint128 => uint256) _data`
 *
 *  For a `mapping(address => mapping(address => Uint256Deque))`,
 *  the deque value slot is
 *  `keccak256(user || keccak256(asset || S_userAssociatedNonces))`. */
const dequeValueSlot = (asset: string, user: string): `0x${string}` => {
  const inner = keccak256(
    encodeAbiParameters(
      [{ type: 'address' }, { type: 'uint256' }],
      [asset as `0x${string}`, KELP_USER_NONCES_SLOT],
    ),
  )
  return keccak256(
    encodeAbiParameters(
      [{ type: 'address' }, { type: 'bytes32' }],
      [user as `0x${string}`, inner],
    ),
  )
}

/**
 * Kelp rsETH — per-asset FIFO deque keyed by `(asset, user)`.
 *
 * Log-free enumeration sequence:
 *  1. For each whitelisted asset, derive the deque value slot via
 *     `keccak256` and read it with `eth_getStorageAt`. The 32-byte
 *     word holds `_begin` (low 128 bits) and `_end` (high 128 bits);
 *     `length = _end - _begin`.
 *  2. Multicall `getUserWithdrawalRequest(asset, user, i)` for
 *     `i in [0, length)`. The contract handles the `_begin` offset
 *     internally — pass plain index, not `i + _begin`.
 *  3. Pull `withdrawalDelayBlocks()` once for the per-request
 *     finalization check (`currentBlock >= withdrawalStartBlock + delay`).
 *
 * Storage slot is brittle to upgrades. Re-verify the slot derivation
 * if the proxy's implementation changes — see
 * `INDEXING_STRATEGIES.md` for the derivation walkthrough.
 */
export const readerKelp: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const client = getEvmClient(chainId)
    const manager = entry.withdrawalContract as `0x${string}`

    // Stage 1 — per-asset deque length via eth_getStorageAt + the
    // single delayBlocks lookup + currentBlock.
    let lengths: { asset: string; length: number }[] = []
    let currentBlock = 0n
    let delayBlocks = 0n
    try {
      const [storageReads, block, delay] = await Promise.all([
        Promise.all(
          KELP_WITHDRAWABLE_ASSETS.map(async (asset) => {
            const slot = dequeValueSlot(asset, user)
            const raw = await client.getStorageAt({ address: manager, slot })
            if (!raw || raw === '0x' || raw === pad('0x00')) {
              return { asset, length: 0 }
            }
            // raw is a 0x-prefixed 32-byte hex string.
            const word = BigInt(raw)
            const begin = word & ((1n << 128n) - 1n)
            const end = (word >> 128n) & ((1n << 128n) - 1n)
            const length = end >= begin ? Number(end - begin) : 0
            return { asset, length }
          }),
        ),
        client.getBlockNumber(),
        (
          multicallRetry({
            chain: chainId,
            calls: [
              {
                address: manager,
                name: 'withdrawalDelayBlocks',
                params: [],
              },
            ] as unknown as never[],
            abi: [KelpWithdrawalManagerAbi] as unknown as never,
          }) as Promise<unknown[]>
        ).then((res) => toBigInt(res[0]) ?? 0n),
      ])
      lengths = storageReads
      currentBlock = block
      delayBlocks = delay
    } catch {
      return []
    }

    const total = lengths.reduce((s, x) => s + x.length, 0)
    if (total === 0) return []

    // Stage 2 — fan out per-asset, per-index detail calls.
    const detailCalls: Array<{
      address: string
      name: string
      params: unknown[]
      _asset: string
    }> = []
    for (const { asset, length } of lengths) {
      for (let i = 0; i < length; i++) {
        detailCalls.push({
          address: manager,
          name: 'getUserWithdrawalRequest',
          params: [asset, user, BigInt(i)],
          _asset: asset,
        })
      }
    }
    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: detailCalls.map(
        ({ _asset: _, ...rest }) => rest,
      ) as unknown as never[],
      abi: detailCalls.map(() => KelpWithdrawalManagerAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < detailCalls.length; i++) {
      const r = stage2[i] as
        | {
            expectedAssetAmount?: unknown
            withdrawalStartBlock?: unknown
            userNonce?: unknown
          }
        | unknown[]
        | undefined
      if (!r) continue
      // viem returns named tuples as plain arrays unless ABI marks
      // outputs with names + uses `viem`'s parser — handle both shapes.
      const expected = Array.isArray(r)
        ? toBigInt(r[1])
        : toBigInt(r.expectedAssetAmount)
      const startBlock = Array.isArray(r)
        ? toBigInt(r[2])
        : toBigInt(r.withdrawalStartBlock)
      const nonce = Array.isArray(r) ? toBigInt(r[3]) : toBigInt(r.userNonce)
      if (expected === undefined || startBlock === undefined) continue
      const claimable = currentBlock >= startBlock + delayBlocks
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        // Composite request id — `(asset, userNonce)` is the natural
        // unit for the claim function. Keep the format human-readable
        // so worker-api / UI can split on `:` if needed.
        requestId: `${detailCalls[i]._asset}:${(nonce ?? 0n).toString()}`,
        amountUnderlying: expected.toString(),
        status: claimable ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
